import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';
import { Stack } from 'ui/stack';

export interface CriticalElementRowSkeletonProps {
  nameSx: SxProps<Theme>;
  metaSx: SxProps<Theme>;
}

const SUMMARY_COLUMN_COUNT = 3;
const SUMMARY_COLUMNS = Array.from(
  { length: SUMMARY_COLUMN_COUNT },
  (_, index) => index
);

const CriticalElementRowSkeleton = ({
  nameSx,
  metaSx,
}: CriticalElementRowSkeletonProps) => (
  <Stack
    direction="row"
    sx={{
      width: '100%',
      alignItems: 'center',
      gap: 1.5,
      borderRadius: '10px',
      borderWidth: '1px',
      borderColor: 'tokens.navy.200',
      px: 2,
      py: 1.5,
    }}
    data-testid="critical-element-row-skeleton"
  >
    <Box
      sx={{
        width: '0.75rem',
        height: '0.75rem',
        flexShrink: 0,
        borderRadius: '0.25rem',
        bgcolor: 'tokens.gray.200',
      }}
    />

    <Stack sx={{ minWidth: 0, flex: '1 1 0%', gap: 0.75 }}>
      <Box
        sx={mergeSx(
          {
            height: '1.25rem',
            maxWidth: '100%',
            borderRadius: '0.25rem',
            bgcolor: 'tokens.gray.200',
          },
          nameSx
        )}
      />
      <Box
        sx={mergeSx(
          {
            height: '1rem',
            maxWidth: '100%',
            borderRadius: '0.25rem',
            bgcolor: 'tokens.gray.100',
          },
          metaSx
        )}
      />
    </Stack>

    <Stack direction="row" sx={{ flexShrink: 0, alignItems: 'center', gap: 2 }}>
      {SUMMARY_COLUMNS.map((column) => (
        <Stack key={column} sx={{ alignItems: 'flex-end', gap: 0.5 }}>
          <Box
            sx={{
              height: '0.75rem',
              width: '2rem',
              borderRadius: '0.25rem',
              bgcolor: 'tokens.gray.100',
            }}
          />
          <Box
            sx={{
              height: '1rem',
              width: '3rem',
              borderRadius: '0.25rem',
              bgcolor: 'tokens.gray.200',
            }}
          />
          <Box
            sx={{
              height: '0.75rem',
              width: '3.5rem',
              borderRadius: '0.25rem',
              bgcolor: 'tokens.gray.100',
            }}
          />
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export default CriticalElementRowSkeleton;
